import { useEffect } from 'react';
import { IChannelApi, IChatApiMessage, IChatMessage } from '@vivli/features/chat/infrastructure/interface';
import { useChatApiContext } from '@vivli/core/infrastructure/context';
import { useChatPubSub } from './use-chat-pub-sub.hook';

interface UseChannelHookProps {
    channelId: string;
    onStart?: (channel: IChannelApi) => void;
    onStop?: () => void;
    onMessage?: ((message: IChatApiMessage) => void) | ((message: IChatMessage) => void);
    onError?: (err) => void;
}

// Real-time chat delivery over Ably (DEV-669). SignalR was removed in DEV-608; this hook restores
// live delivery by subscribing to the channel's Ably broadcast (via useChatPubSub) and pushing each
// incoming message to `onMessage`, while still exposing the REST-backed `send` API and the start/stop
// lifecycle so consumers keep working unchanged.
export const useChannel = ({ channelId, onStart, onStop, onMessage }: UseChannelHookProps): void => {
    const chatService = useChatApiContext();

    const send = (message: IChatMessage, sendToLocal?: boolean) => {
        if (sendToLocal) {
            // @ts-expect-error ignore, works as intended
            onMessage && onMessage(message);
        }

        return chatService.postChatMessage(channelId, message.text);
    };

    useChatPubSub(channelId, (message) => {
        // onMessage accepts IChatApiMessage | IChatMessage; a broadcast is an IChatApiMessage (assignable to both).
        onMessage && onMessage(message);
    });

    useEffect(() => {
        if (!channelId) {
            return;
        }

        onStart && onStart({ send });

        return () => {
            onStop && onStop();
        };
    }, [channelId]);
};
